/**
 * Module quản lý phím tắt tập trung (Hotkey Manager)
 * Hỗ trợ điều hướng trên cả 3 giao diện ngụy trang: Google Sheets, Gmail Web, Zalo Web.
 * 
 * Danh mục phím tắt hỗ trợ:
 * 1. 🔍 Tìm kiếm truyện: Ctrl + K, Alt + S, hoặc phím '/' (khi không ở ô input)
 * 2. ⏩ Chương tiếp theo: Ctrl + Mũi tên phải, Alt + N, hoặc phím ']'
 * 3. ⏪ Chương trước đó: Ctrl + Mũi tên trái, Alt + P, hoặc phím '['
 * 4. 🎭 Đổi giao diện ngụy trang: F4 hoặc Alt + T
 * 5. 🚨 Thoát hiểm khẩn cấp (Panic Exit): Alt + Q, F2 (Chuyển hướng sang Google / Đóng tab)
 * 6. 🌐 Mở tab làm việc an toàn: Alt + W (Mở Google Trang tính mới)
 * 7. ❓ Bảng hướng dẫn phím tắt: F1 hoặc Shift + ?
 */
export class HotkeyManagerModule {
  constructor(storyReader, themeManager, searchModal, bossKey) {
    this.storyReader = storyReader;
    this.themeManager = themeManager;
    this.searchModal = searchModal;
    this.bossKey = bossKey;

    // DOM Elements của Modal hướng dẫn Hotkey
    this.hotkeyModal = document.getElementById('hotkey-guide-modal');
    this.btnCloseModal = document.getElementById('hotkey-modal-close-btn');

    // Các nút kích hoạt xem hướng dẫn trên header các giao diện
    this.btnHelpHome = document.getElementById('ws-btn-help');
    this.btnHelpSheets = document.getElementById('btn-help-sheets');
    this.btnHelpGmail = document.getElementById('btn-help-gmail');
    this.btnHelpZalo = document.getElementById('btn-help-zalo');
    this.btnHelpZaloSidebar = document.getElementById('btn-help-zalo-sidebar');

    this.bindEvents();
  }

  /**
   * Đăng ký toàn bộ sự kiện bàn phím và click
   */
  bindEvents() {
    // 1. Lắng nghe phím bấm toàn cục trên window
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));

    // 2. Sự kiện mở modal hướng dẫn từ các nút trên UI
    const openGuide = () => this.openGuideModal();
    if (this.btnHelpHome) this.btnHelpHome.addEventListener('click', openGuide);
    if (this.btnHelpSheets) this.btnHelpSheets.addEventListener('click', openGuide);
    if (this.btnHelpGmail) this.btnHelpGmail.addEventListener('click', openGuide);
    if (this.btnHelpZalo) this.btnHelpZalo.addEventListener('click', openGuide);
    if (this.btnHelpZaloSidebar) this.btnHelpZaloSidebar.addEventListener('click', openGuide);

    // 3. Sự kiện đóng modal hướng dẫn
    if (this.btnCloseModal) {
      this.btnCloseModal.addEventListener('click', () => this.closeGuideModal());
    }

    if (this.hotkeyModal) {
      this.hotkeyModal.addEventListener('click', (e) => {
        if (e.target === this.hotkeyModal) {
          this.closeGuideModal();
        }
      });
    }
  }

  /**
   * Kiểm tra người dùng có đang gõ văn bản trong input/textarea không
   */
  isInputFocused() {
    const active = document.activeElement;
    if (!active) return false;
    const tagName = active.tagName.toLowerCase();
    return tagName === 'input' || tagName === 'textarea' || active.isContentEditable;
  }

  /**
   * Xử lý bộ phím tắt thông minh
   * @param {KeyboardEvent} e
   */
  handleKeyDown(e) {
    const isTyping = this.isInputFocused();

    // ----------------------------------------------------
    // NHÓM 1: BẢNG HƯỚNG DẪN PHÍM TẮT (F1 HOẶC Shift + ?)
    // ----------------------------------------------------
    if (e.key === 'F1' || (e.shiftKey && (e.key === '?' || e.key === '/'))) {
      e.preventDefault();
      this.toggleGuideModal();
      return;
    }

    // ----------------------------------------------------
    // NHÓM 2: THOÁT HIỂM KHẨN CẤP / TRÁNH BỊ PHÁT HIỆN
    // ----------------------------------------------------
    // Alt + Q hoặc F2: Thoát hiểm cấp tốc (chuyển hướng sang Google / đóng tab)
    if ((e.altKey && (e.key === 'q' || e.key === 'Q')) || e.key === 'F2') {
      e.preventDefault();
      this.panicExit();
      return;
    }

    // Alt + W: Mở tab làm việc an toàn mới
    if (e.altKey && (e.key === 'w' || e.key === 'W')) {
      e.preventDefault();
      this.openSafeWorkTab();
      return;
    }

    // Alt + H: Trở về Trang chủ Workspace Portal trung tâm
    if (e.altKey && (e.key === 'h' || e.key === 'H')) {
      e.preventDefault();
      window.location.hash = '#/home';
      return;
    }

    // ----------------------------------------------------
    // NHÓM 3: ĐỔI GIAO DIỆN NGỤY TRANG (F4 HOẶC Alt + T)
    // ----------------------------------------------------
    if (e.key === 'F4' || (e.altKey && (e.key === 't' || e.key === 'T'))) {
      e.preventDefault();
      if (this.themeManager) {
        this.themeManager.openModal();
      }
      return;
    }

    // ----------------------------------------------------
    // NHÓM 4: TÌM KIẾM TRUYỆN (Ctrl + K, Alt + S, hoặc phím '/' khi không focus input)
    // ----------------------------------------------------
    if ((e.ctrlKey && (e.key === 'k' || e.key === 'K')) || (e.altKey && (e.key === 's' || e.key === 'S'))) {
      e.preventDefault();
      if (this.searchModal) {
        this.searchModal.open();
      }
      return;
    }

    if (!isTyping && e.key === '/') {
      e.preventDefault();
      if (this.searchModal) {
        this.searchModal.open();
      }
      return;
    }

    // ----------------------------------------------------
    // NHÓM 5: CHUYỂN CHƯƠNG (NEXT / PREV CHAPTER)
    // ----------------------------------------------------
    // Chương tiếp theo: Ctrl + ArrowRight, Alt + N, hoặc phím ']' (khi không typing)
    const isNextKey = (e.ctrlKey && e.key === 'ArrowRight') ||
                      (e.altKey && (e.key === 'n' || e.key === 'N')) ||
                      (!isTyping && e.key === ']');
    if (isNextKey) {
      e.preventDefault();
      if (this.storyReader) {
        this.storyReader.navigateChapter('next');
      }
      return;
    }

    // Chương trước đó: Ctrl + ArrowLeft, Alt + P, hoặc phím '[' (khi không typing)
    const isPrevKey = (e.ctrlKey && e.key === 'ArrowLeft') ||
                      (e.altKey && (e.key === 'p' || e.key === 'P')) ||
                      (!isTyping && e.key === '[');
    if (isPrevKey) {
      e.preventDefault();
      if (this.storyReader) {
        this.storyReader.navigateChapter('prev');
      }
      return;
    }
  }

  /**
   * Thoát hiểm tức thì:
   * Thử đóng tab hiện tại; nếu trình duyệt chặn window.close(), lập tức chuyển hướng URL sang Google Trang tính
   */
  panicExit() {
    console.log('[Panic Exit] Kích hoạt thoát hiểm khẩn cấp!');
    try {
      // 1. Thử đóng tab
      window.close();
    } catch (err) {
      console.warn('Không thể đóng tab trực tiếp:', err);
    }

    // 2. Chuyển hướng ngay lập tức sang trang công việc an toàn (Google Trang tính)
    window.location.replace('https://docs.google.com/spreadsheets/u/0/');
  }

  /**
   * Mở tab công việc mới an toàn và chuyển đổi tab
   */
  openSafeWorkTab() {
    console.log('[Safe Tab] Mở tab làm việc mới an toàn!');
    // Mở một tab Google Sheets hoặc Google thật sự
    const newWindow = window.open('https://docs.google.com/spreadsheets/u/0/', '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Nếu bị chặn popup, chuyển hướng trang hiện tại
      window.location.replace('https://docs.google.com/spreadsheets/u/0/');
    }
  }

  /**
   * Mở modal bảng hướng dẫn phím tắt
   */
  openGuideModal() {
    if (this.hotkeyModal) {
      this.hotkeyModal.style.display = 'flex';
    }
  }

  /**
   * Đóng modal bảng hướng dẫn phím tắt
   */
  closeGuideModal() {
    if (this.hotkeyModal) {
      this.hotkeyModal.style.display = 'none';
    }
  }

  /**
   * Bật/tắt modal bảng hướng dẫn phím tắt
   */
  toggleGuideModal() {
    if (!this.hotkeyModal) return;
    if (this.hotkeyModal.style.display === 'flex') {
      this.closeGuideModal();
    } else {
      this.openGuideModal();
    }
  }
}
